import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { MapPin, X, Clock, Calendar, Bell, AlertTriangle, CreditCard, Users, Cake, FileText, RefreshCw, CheckSquare, User, Home, AlertCircle, Newspaper, Settings, Eye, TrendingUp, BarChart3, DollarSign } from 'lucide-react';
import { collection, getDocs, doc, getDoc, query, where, getFirestore, updateDoc, writeBatch, orderBy, limit, onSnapshot, Timestamp } from 'firebase/firestore';
import { runNormalization } from './normalizeAppointmentsDates';
import FinancialSummaryWidget from './FinancialSummaryWidget';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useTheme } from '../contexts/ThemeContext';
import '../Dashboard.css';

// Composants définis en dehors pour éviter la perte de focus
const Widget = ({ title, children, icon: Icon }) => (
  <div className="bg-white p-5 rounded-lg shadow-sm border border-gray-100">
    <div className="flex justify-between items-center mb-3">
      <h3 className="text-lg font-semibold text-gray-700 flex items-center">
        {Icon && <Icon size={18} className="mr-2 text-gray-400" />}
        {title}
      </h3>
    </div>
    {children}
  </div>
);

const TileWrapper = ({ children, className = "" }) => (
  <div className={`col-span-1 ${className}`}>
    {children}
  </div>
);

const PopupNavigation = ({ isOpen, onClose, address }) => {
  if (!isOpen) return null;

  const handleGoogleMapsNavigate = () => {
    const encodedAddress = encodeURIComponent(address);
    window.open(`https://www.google.com/maps/search/?api=1&query=${encodedAddress}`, '_blank');
  };

  const handleWazeNavigate = () => {
    const encodedAddress = encodeURIComponent(address);
    window.open(`https://www.waze.com/ul?q=${encodedAddress}&navigate=yes`, '_blank');
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-xl max-w-sm w-full">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Navigation</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200">
            <X size={20} />
          </button>
        </div>
        <p className="mb-4 text-gray-700 dark:text-gray-300">Adresse du client : {address}</p>
        <div className="space-y-2">
          <button
            onClick={handleGoogleMapsNavigate}
            className="w-full bg-blue-500 text-white py-2 px-4 rounded hover:bg-blue-600 transition-colors"
          >
            Ouvrir dans Google Maps
          </button>
          <button
            onClick={handleWazeNavigate}
            className="w-full bg-green-500 text-white py-2 px-4 rounded hover:bg-green-600 transition-colors"
          >
            Ouvrir dans Waze
          </button>
        </div>
      </div>
    </div>
  );
};

const Tile = ({ title, children, icon: Icon, className = "" }) => (
  <div className={`bg-white p-4 rounded-lg shadow-sm ${className}`}>
    {title && (
      <h2 className="text-lg font-semibold text-gray-700 mb-3 flex items-center">
        {Icon && <Icon size={18} className="mr-2 text-gray-400" />}
        {title}
      </h2>
    )}
    <div>{children}</div>
  </div>
);

const PremiumTile = ({ title, children, icon: Icon, className = "", color = "slate", delay = 0, onClick }) => {
  const gradients = {
    blue: "from-blue-50 to-white dark:from-blue-900/40 dark:to-slate-900/80",
    cyan: "from-cyan-50 to-white dark:from-cyan-900/40 dark:to-slate-900/80",
    purple: "from-purple-50 to-white dark:from-purple-900/40 dark:to-slate-900/80",
    emerald: "from-emerald-50 to-white dark:from-emerald-900/40 dark:to-slate-900/80",
    rose: "from-rose-50 to-white dark:from-rose-900/40 dark:to-slate-900/80",
    amber: "from-amber-50 to-white dark:from-amber-900/40 dark:to-slate-900/80",
    slate: "from-gray-50 to-white dark:from-slate-800/60 dark:to-slate-900/80",
    indigo: "from-indigo-50 to-white dark:from-indigo-900/40 dark:to-slate-900/80"
  };

  const glows = {
    blue: "bg-blue-400",
    cyan: "bg-cyan-400",
    purple: "bg-purple-400",
    emerald: "bg-emerald-400",
    rose: "bg-rose-400",
    amber: "bg-amber-400",
    slate: "bg-slate-400",
    indigo: "bg-indigo-400"
  };

  const selectedGradient = gradients[color] || gradients.slate;
  const selectedGlow = glows[color] || glows.slate;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: delay }}
      className={`relative overflow-hidden rounded-2xl p-6 shadow-lg border border-gray-200 dark:border-slate-700/50 backdrop-blur-xl ${className} bg-gradient-to-br ${selectedGradient} ${onClick ? 'cursor-pointer transform hover:scale-[1.02] transition-all' : ''}`}
      onClick={onClick}
    >
      <div className={`absolute -right-6 -top-6 h-32 w-32 rounded-full opacity-10 blur-3xl ${selectedGlow}`} />

      {title && (
        <div className="relative z-10 flex justify-between items-start mb-4">
          <h2 className="text-lg font-bold text-gray-800 dark:text-white tracking-wide text-shadow-sm flex items-center gap-2">
            {title}
          </h2>
          {Icon && (
            <div className={`p-2 rounded-lg backdrop-blur-sm shadow-inner ${color === 'blue' ? 'bg-blue-500/20 text-blue-500 dark:text-blue-400' :
                color === 'cyan' ? 'bg-cyan-500/20 text-cyan-500 dark:text-cyan-400' :
                  color === 'purple' ? 'bg-purple-500/20 text-purple-500 dark:text-purple-400' :
                    color === 'emerald' ? 'bg-emerald-500/20 text-emerald-500 dark:text-emerald-400' :
                      color === 'rose' ? 'bg-rose-500/20 text-rose-500 dark:text-rose-400' :
                        color === 'amber' ? 'bg-amber-500/20 text-amber-500 dark:text-amber-400' :
                          'bg-slate-500/20 text-slate-500 dark:text-slate-400'
              }`}>
              <Icon size={20} />
            </div>
          )}
        </div>
      )}
      <div className="relative z-10">
        {children}
      </div>
    </motion.div>
  );
};

// Fonction pour formater une adresse (objet ou chaîne)
const formatAddress = (address) => {
  if (typeof address === 'string') return address;
  if (typeof address === 'object' && address !== null) {
    const { street, city, postalCode, country } = address;
    const parts = [];
    if (street) parts.push(street);
    if (postalCode) parts.push(postalCode);
    if (city) parts.push(city);
    if (country) parts.push(country);
    return parts.join(', ');
  }
  return 'Adresse non spécifiée';
};

// Mapping des noms affichables pour les cartes
const tileDisplayNames = {
  dateTime: "Date & Heure",
  monEspace: "Mon espace",
  vueEnsemble: "Vue d'ensemble",
  prochainRendezVous: "Prochains rendez-vous",
  gestionCollaborateurs: "Gestion des collaborateurs",
  suiviPresences: "Présences",
  sommairePaie: "Sommaire paie",
  anniversaires: "Anniversaires à venir",
  suiviDocuments: "Documents",
  suiviTaches: "Tâches",
  suiviFactures: "Factures",
  suiviDevis: "Devis",
  suiviContacts: "Contacts",
  rapportsIntervenants: "Rapports intervenants"
};

// Modal de personnalisation avec le thème de l'application
const CustomizeModal = ({ isOpen, onClose, preferences, onSave, roleBasedTiles }) => {
  const [localPreferences, setLocalPreferences] = useState({});

  // Initialiser les préférences locales quand le modal s'ouvre
  useEffect(() => {
    if (isOpen) {
      // Créer un objet complet avec TOUTES les tuiles
      const allTiles = {
        dateTime: true,
        monEspace: true,
        vueEnsemble: true,
        prochainRendezVous: true,
        gestionCollaborateurs: true,
        suiviPresences: true,
        sommairePaie: true,
        anniversaires: true,
        suiviDocuments: true,
        suiviTaches: true,
        suiviFactures: true,
        suiviDevis: true,
        suiviContacts: true,
        rapportsIntervenants: true
      };

      // Fusionner avec les préférences existantes
      const completePreferences = { ...allTiles, ...preferences };
      setLocalPreferences(completePreferences);
    }
  }, [isOpen, preferences, roleBasedTiles]);

  // Fonction pour vérifier si une tuile est modifiable selon le rôle
  const isTileModifiable = (tileKey) => {
    // Si pas de restrictions de rôle, toutes les tuiles sont modifiables
    if (!roleBasedTiles || Object.keys(roleBasedTiles).length === 0) {
      return true;
    }

    // Si le rôle autorise cette tuile (true ou undefined), elle est modifiable
    // Si le rôle interdit cette tuile (false), elle n'est pas modifiable
    return roleBasedTiles[tileKey] !== false;
  };

  const handleToggle = (key) => {
    // Ne pas permettre de modifier les tuiles interdites par le rôle
    if (!isTileModifiable(key)) {
      return;
    }

    setLocalPreferences(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleSave = () => {
    // S'assurer que les préférences ne sont pas vides
    const validPreferences = Object.keys(localPreferences).reduce((acc, key) => {
      // Appliquer les restrictions de rôle avant sauvegarde
      if (roleBasedTiles && roleBasedTiles[key] === false) {
        // Forcer la désactivation des tuiles interdites
        acc[key] = false;
      } else {
        acc[key] = localPreferences[key] !== undefined ? localPreferences[key] : true;
      }
      return acc;
    }, {});

    onSave(validPreferences);
    onClose();
  };

  if (!isOpen) return null;

  // Utiliser les préférences par défaut si localPreferences est vide
  const displayPreferences = Object.keys(localPreferences).length > 0 ? localPreferences : {
    dateTime: true,
    monEspace: true,
    prochainRendezVous: true,
    gestionCollaborateurs: true,
    suiviPresences: true,
    sommairePaie: true,
    anniversaires: true,
    suiviDocuments: true,
    suiviTaches: true,
    suiviFactures: true,
    suiviDevis: true,
    suiviContacts: true
  };

  return (
    <div className="fixed inset-0 backdrop-blur-sm flex items-center justify-center z-50 p-4" style={{ backgroundColor: 'rgba(0, 0, 0, 0.6)' }}>
      <div className="max-w-md w-full max-h-[80vh] overflow-hidden rounded-2xl shadow-2xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900">

        {/* Header */}
        <div className="px-6 py-5 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-slate-800 dark:to-slate-800 border-b border-gray-200 dark:border-slate-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-3 h-3 rounded-full bg-gradient-to-r from-blue-500 to-indigo-500 animate-pulse"></div>
              <h2 className="text-xl font-semibold tracking-tight text-gray-900 dark:text-white">
                Personnalisation
              </h2>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-lg text-gray-500 dark:text-slate-400 hover:bg-gray-100 dark:hover:bg-slate-700 hover:text-gray-700 dark:hover:text-white transition-colors"
            >
              <X size={20} />
            </button>
          </div>
          <p className="text-sm mt-2 text-gray-500 dark:text-slate-400">
            Sélectionnez les cartes à afficher sur votre tableau de bord
          </p>
        </div>

        {/* Liste des cartes avec scroll */}
        <div className="px-6 py-4 max-h-96 overflow-y-auto">
          <div className="space-y-2">
            {Object.entries(displayPreferences).map(([key, value]) => {
              const isModifiable = isTileModifiable(key);
              const isActive = value !== undefined ? value : true;

              return (
                <div
                  key={key}
                  onClick={() => handleToggle(key)}
                  className={`flex items-center justify-between p-4 rounded-xl border transition-all ${
                    isModifiable
                      ? 'cursor-pointer bg-gray-50 dark:bg-slate-800/60 border-gray-200 dark:border-slate-700 hover:bg-blue-50 dark:hover:bg-slate-700/80 hover:border-blue-300 dark:hover:border-blue-500/50'
                      : 'cursor-not-allowed bg-red-50/50 dark:bg-red-900/10 border-red-200 dark:border-red-800/40 opacity-70'
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <span className={`font-medium text-sm tracking-wide ${
                      isModifiable
                        ? 'text-gray-800 dark:text-slate-200'
                        : 'text-red-600 dark:text-red-400'
                    }`}>
                      {tileDisplayNames[key] || key}
                    </span>
                    {!isModifiable && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 font-medium">
                        Restreint
                      </span>
                    )}
                  </div>

                  {/* Toggle switch */}
                  <div className={`relative w-11 h-6 rounded-full transition-colors duration-200 flex-shrink-0 ${
                    !isModifiable
                      ? 'bg-red-200 dark:bg-red-800/40'
                      : isActive
                        ? 'bg-blue-500 dark:bg-blue-600'
                        : 'bg-gray-300 dark:bg-slate-600'
                  }`}>
                    <div className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform duration-200 ${
                      isActive ? 'translate-x-5' : 'translate-x-0'
                    }`} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Actions */}
        <div className="px-6 py-4 bg-gray-50 dark:bg-slate-800/50 border-t border-gray-200 dark:border-slate-700">
          <div className="flex space-x-3">
            <button
              onClick={onClose}
              className="flex-1 py-2.5 px-4 rounded-xl border border-gray-300 dark:border-slate-600 text-gray-700 dark:text-slate-200 bg-white dark:bg-slate-800 hover:bg-gray-100 dark:hover:bg-slate-700 font-medium text-sm transition-colors"
            >
              Annuler
            </button>
            <button
              onClick={handleSave}
              className="flex-1 py-2.5 px-4 rounded-xl bg-blue-500 hover:bg-blue-600 dark:bg-blue-600 dark:hover:bg-blue-700 text-white font-medium text-sm transition-colors shadow-sm"
            >
              Appliquer
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const Dashboard = ({ db, currentUser, roleBasedTiles }) => {
  const { theme } = useTheme();
  const [currentTime, setCurrentTime] = useState(new Date());
  const [activeEmployees, setActiveEmployees] = useState(0);
  const [normalizationStatus, setNormalizationStatus] = useState('');
  const [updateStatus, setUpdateStatus] = useState('');
  const [appointments, setAppointments] = useState([]);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [todayAppointmentsCount, setTodayAppointmentsCount] = useState(0);
  const [tomorrowAppointmentsCount, setTomorrowAppointmentsCount] = useState(0);
  const navigate = useNavigate();
  const [occupationRate, setOccupationRate] = useState(0);
  const [nextAppointmentTime, setNextAppointmentTime] = useState('');
  const [tilePreferences, setTilePreferences] = useState({});
  const [showCustomizeModal, setShowCustomizeModal] = useState(false);
  const [preferencesLoading, setPreferencesLoading] = useState(true);
  const [facturesData, setFacturesData] = useState({
    total: 0,
    enAttente: 0,
    enRetard: 0,
    montantEnAttente: 0,
    montantEnRetard: 0,
    dernieresFactures: []
  });

  const formatDate = (date) => {
    const options = { weekday: 'short', year: 'numeric', month: 'long', day: 'numeric' };
    return date.toLocaleDateString('fr-FR', options);
  };

  const formatTime = (date) => {
    return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  };

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const fetchActiveEmployees = async () => {
      const employeesRef = collection(db, "employees");
      const activeEmployeesQuery = query(employeesRef, where("isArchived", "==", false));
      const querySnapshot = await getDocs(activeEmployeesQuery);
      setActiveEmployees(querySnapshot.size);
    };

    fetchActiveEmployees();
  }, [db]);

  const handleNormalization = async () => {
    setNormalizationStatus('En cours...');
    try {
      await runNormalization(db);
      setNormalizationStatus('Normalisation terminée avec succès.');
    } catch (error) {
      setNormalizationStatus('Erreur lors de la normalisation. Veuillez réessayer.');
    }
  };


  // Dans le useEffect de récupération des rendez-vous
  useEffect(() => {
    if (!db || !currentUser?.id) return;

    try {
      const appointmentsRef = collection(db, 'appointments');
      const now = new Date();
      const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const startOfTomorrow = new Date(startOfToday);
      startOfTomorrow.setDate(startOfTomorrow.getDate() + 1);
      const endOfTomorrow = new Date(startOfTomorrow);
      endOfTomorrow.setHours(23, 59, 59, 999);

      const q = query(
        appointmentsRef,
        where('employeeId', '==', currentUser.id),
        where('date', '>=', Timestamp.fromDate(startOfToday)),
        where('date', '<=', Timestamp.fromDate(endOfTomorrow)),
        orderBy('date', 'asc'),
        orderBy('startTime', 'asc')
      );

      const unsubscribe = onSnapshot(q, async (querySnapshot) => {
        const now = new Date();
        const appointmentsWithClientInfo = await Promise.all(
          querySnapshot.docs.map(async (appointmentDoc) => {
            const appointmentData = appointmentDoc.data();
            let clientData = {};

            if (appointmentData.clientId) {
              const clientDocRef = doc(db, 'clients', appointmentData.clientId);
              const clientSnap = await getDoc(clientDocRef);
              if (clientSnap.exists()) {
                clientData = clientSnap.data();
              }
            }

            // S'assurer que les données de date et d'heure existent
            if (!appointmentData.date || !appointmentData.startTime || !appointmentData.endTime) {
              return null;
            }

            const appointmentDate = appointmentData.date?.toDate();
            const [startHours, startMinutes] = appointmentData.startTime.split(':');
            const [endHours, endMinutes] = appointmentData.endTime.split(':');

            const startDateTime = new Date(appointmentDate);
            startDateTime.setHours(parseInt(startHours), parseInt(startMinutes), 0);

            const endDateTime = new Date(appointmentDate);
            endDateTime.setHours(parseInt(endHours), parseInt(endMinutes), 0);

            return {
              id: appointmentDoc.id,
              ...appointmentData,
              date: appointmentDate,
              startDateTime,
              endDateTime,
              clientName: `${clientData.lastName || ''} ${clientData.firstName || ''}`.trim(),
              clientAddress: clientData.address || '',
              addressNumber: clientData.addressNumber || ''
            };
          })
        );

        // Ne garder que les rendez-vous en cours ou à venir
        const filteredAppointments = appointmentsWithClientInfo.filter(appointment => {
          if (!appointment) return false;
          const now = new Date();
          // Garder si le rendez-vous est en cours ou n'a pas encore commencé
          return appointment.startDateTime >= now ||
            (now >= appointment.startDateTime && now <= appointment.endDateTime);
        });

        // Trier par date et heure
        const sortedAppointments = filteredAppointments.sort((a, b) => {
          return a.startDateTime - b.startDateTime;
        });

        // Mise à jour des compteurs
        const todayCount = sortedAppointments.filter(app => {
          const appDate = app.date;
          return (
            appDate.getFullYear() === now.getFullYear() &&
            appDate.getMonth() === now.getMonth() &&
            appDate.getDate() === now.getDate()
          );
        }).length;

        const tomorrow = new Date(now);
        tomorrow.setDate(tomorrow.getDate() + 1);
        const tomorrowCount = sortedAppointments.filter(app => {
          const appDate = app.date;
          return (
            appDate.getFullYear() === tomorrow.getFullYear() &&
            appDate.getMonth() === tomorrow.getMonth() &&
            appDate.getDate() === tomorrow.getDate()
          );
        }).length;

        setTodayAppointmentsCount(todayCount);
        setTomorrowAppointmentsCount(tomorrowCount);
        setAppointments(sortedAppointments);
      });


      return () => unsubscribe();
    } catch (error) {
      // Erreur silencieuse lors de la récupération des rendez-vous
    }
  }, [db, currentUser]);


  // Fonction d'aide pour formater la date
  const formatAppointmentDate = (date, startTime) => {
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    // Comparaison des dates sans tenir compte de l'heure
    const isToday = date.toDateString() === today.toDateString();
    const isTomorrow = date.toDateString() === tomorrow.toDateString();

    if (isToday) {
      return `Aujourd'hui ${startTime}`;
    } else if (isTomorrow) {
      return `Demain ${startTime}`;
    } else {
      // Pour les autres dates, format : "Lun. 6 Nov."
      return format(date, 'EEE d MMM', { locale: fr });
    }
  };


  const calculateOccupationRate = useCallback(() => {
    if (appointments.length === 0) return 0;

    const now = new Date();
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0);
    const todayEnd = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);

    const todayAppointments = appointments.filter(app =>
      app.date >= todayStart && app.date <= todayEnd
    );

    const totalMinutes = (todayEnd - todayStart) / (1000 * 60);
    let occupiedMinutes = 0;

    todayAppointments.forEach(app => {
      const start = new Date(app.date);
      start.setHours(parseInt(app.startTime.split(':')[0]), parseInt(app.startTime.split(':')[1]));
      const end = new Date(app.date);
      end.setHours(parseInt(app.endTime.split(':')[0]), parseInt(app.endTime.split(':')[1]));
      occupiedMinutes += (end - start) / (1000 * 60);
    });

    return Math.round((occupiedMinutes / totalMinutes) * 100);
  }, [appointments]);

  // Charger les données des factures
  const chargerFactures = useCallback(async () => {
    if (!db) return;

    try {
      // Charger les factures mandat (stripeBillingInvoices)
      const facturesRef = collection(db, 'stripeBillingInvoices');
      const snapshot = await getDocs(facturesRef);

      const factures = snapshot.docs
        .map(doc => ({
          id: doc.id,
          ...doc.data()
        }))
        .filter(f => !f.deleted); // Exclure les factures supprimées

      // Trier par date de génération (plus récentes en premier)
      factures.sort((a, b) => {
        const dateA = a.dateGeneration?.toDate?.() || new Date(0);
        const dateB = b.dateGeneration?.toDate?.() || new Date(0);
        return dateB - dateA;
      });

      // Calculer les statistiques
      const stats = {
        total: factures.length,
        enAttente: factures.filter(f => f.statut === 'envoyée' || f.statut === 'generée').length,
        enRetard: factures.filter(f => f.statut === 'en_retard').length,
        montantEnAttente: factures
          .filter(f => f.statut === 'envoyée' || f.statut === 'generée')
          .reduce((sum, f) => sum + (f.calcul?.montantTotal || 0), 0),
        montantEnRetard: factures
          .filter(f => f.statut === 'en_retard')
          .reduce((sum, f) => sum + (f.calcul?.montantTotal || 0), 0),
        dernieresFactures: factures.slice(0, 3)
      };

      setFacturesData(stats);
    } catch (error) {
      // Erreur silencieuse lors du chargement des factures
    }
  }, [db]);

  const getNextAppointmentTime = useCallback(() => {
    const now = new Date();
    const nextAppointment = appointments.find(app => {
      const appStart = new Date(app.date);
      appStart.setHours(parseInt(app.startTime.split(':')[0]), parseInt(app.startTime.split(':')[1]));
      return appStart > now;
    });

    if (nextAppointment) {
      return nextAppointment.startTime;
    } else {
      return 'Aucun';
    }
  }, [appointments]);

  useEffect(() => {
    setOccupationRate(calculateOccupationRate());
    setNextAppointmentTime(getNextAppointmentTime());
  }, [appointments, calculateOccupationRate, getNextAppointmentTime]);

  const loadTilePreferences = useCallback(async () => {
    setPreferencesLoading(true);

    // Vérification plus stricte des paramètres
    if (!db || !currentUser?.id) {
      const defaultPreferences = {
        dateTime: true,
        monEspace: true,
        prochainRendezVous: true,
        gestionCollaborateurs: true,
        suiviPresences: true,
        sommairePaie: true,
        anniversaires: true,
        suiviDocuments: true,
        suiviTaches: true,
        suiviFactures: true,
        suiviDevis: true,
        suiviContacts: true
      };
      setTilePreferences(defaultPreferences);
      setPreferencesLoading(false);
      return defaultPreferences;
    }

    try {
      const userDoc = await getDoc(doc(db, 'employees', currentUser.id));
      const defaultPreferences = {
        dateTime: true,
        monEspace: true,
        prochainRendezVous: true,
        gestionCollaborateurs: true,
        suiviPresences: true,
        sommairePaie: true,
        anniversaires: true,
        suiviDocuments: true,
        suiviTaches: true,
        suiviFactures: true,
        suiviDevis: true,
        suiviContacts: true
      };

      let finalPreferences = defaultPreferences;

      if (userDoc.exists()) {
        const userData = userDoc.data();

        if (userData.tilePreferences && Object.keys(userData.tilePreferences).length > 0) {
          // Fusionner les préférences existantes avec les valeurs par défaut
          finalPreferences = { ...defaultPreferences, ...userData.tilePreferences };
        } else {
          try {
            await updateDoc(doc(db, 'employees', currentUser.id), {
              tilePreferences: defaultPreferences
            });
          } catch (error) {
            // Erreur silencieuse lors de l'initialisation
          }
        }
      }

      setTilePreferences(finalPreferences);
      setPreferencesLoading(false);
      return finalPreferences;

    } catch (error) {
      // Définir les préférences par défaut en cas d'erreur
      const defaultPreferences = {
        dateTime: true,
        monEspace: true,
        prochainRendezVous: true,
        gestionCollaborateurs: true,
        suiviPresences: true,
        sommairePaie: true,
        anniversaires: true,
        suiviDocuments: true,
        suiviTaches: true,
        suiviFactures: true,
        suiviDevis: true,
        suiviContacts: true
      };
      setTilePreferences(defaultPreferences);
      setPreferencesLoading(false);
      return defaultPreferences;
    }
  }, [db, currentUser]);

  useEffect(() => {
    // Charger les préférences utilisateur d'abord
    loadTilePreferences().then((loadedPreferences) => {
      // Si des templates basés sur le rôle sont fournis, les appliquer après le chargement
      if (roleBasedTiles && Object.keys(roleBasedTiles).length > 0) {
        // Appliquer les restrictions du rôle aux préférences chargées
        const filteredPreferences = filterPreferencesByRole(loadedPreferences, roleBasedTiles);
        setTilePreferences(filteredPreferences);
      }
      // NE PAS remettre preferencesLoading à false ici - il est déjà géré dans loadTilePreferences
    });

    // Charger les données des factures
    chargerFactures();
  }, [loadTilePreferences, roleBasedTiles, chargerFactures]);

  // Fonction pour filtrer les préférences selon les templates de rôle
  const filterPreferencesByRole = (userPreferences, roleTiles) => {
    const filtered = {};

    // Pour chaque tuile, vérifier si elle est autorisée par le rôle
    Object.keys(userPreferences).forEach(tileKey => {
      // Si le rôle n'autorise pas cette tuile, la désactiver
      if (roleTiles[tileKey] === false) {
        filtered[tileKey] = false;
      } else {
        // Sinon, utiliser la préférence utilisateur
        filtered[tileKey] = userPreferences[tileKey];
      }
    });

    return filtered;
  };


  // Fonction d'aide pour formater la date


  // Modification de la fonction renderAppointment
  const renderAppointment = (appointment) => {
    const now = new Date();
    const isOngoing = now >= appointment.startDateTime && now < appointment.endDateTime;

    const fullAddress = appointment.addressNumber
      ? `${formatAddress(appointment.clientAddress)}, ${appointment.addressNumber}`
      : formatAddress(appointment.clientAddress);

    return (
      <div key={appointment.id} className={`appointment-item ${isOngoing ? 'bg-purple-500/20 dark:bg-purple-900/30 text-white' : 'text-slate-300'
        }`}>
        <div className="appointment-info">
          <div className="flex items-center">
            <User size={16} className="text-gray-400 dark:text-gray-500 mr-2" />
            <div>
              <div className="flex items-center gap-2">
                <span className="appointment-client font-bold">{appointment.clientName}</span>
                {isOngoing && (
                  <span className="px-2 py-1 text-xs bg-purple-500 text-white rounded-full">
                    En cours
                  </span>
                )}
              </div>
              <div className="appointment-address text-slate-400">{fullAddress}</div>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <div className="text-right">
            <div className="appointment-time text-slate-300">
              {formatAppointmentDate(appointment.date, appointment.startTime)}
            </div>
          </div>
          <button
            onClick={() => setSelectedAppointment(appointment)}
            className="p-1 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-full"
          >
            <MapPin size={16} className="text-blue-400" />
          </button>
        </div>
      </div>
    );
  };

  const calculateRemainingDaysInMonth = () => {
    const today = new Date();
    const lastDayOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();
    const currentDay = today.getDate();
    return lastDayOfMonth - currentDay;
  };

  // Modifier également la fonction saveTilePreferences pour respecter les restrictions du rôle
  const saveTilePreferences = async (newPreferences) => {
    if (!db || !currentUser?.id) {
      return;
    }

    try {
      // Fusionner les nouvelles préférences avec les anciennes au lieu de les écraser
      const mergedPreferences = { ...tilePreferences, ...newPreferences };

      // Filtrer les préférences fusionnées selon les templates de rôle
      const filteredPreferences = roleBasedTiles ?
        filterPreferencesByRole(mergedPreferences, roleBasedTiles) :
        mergedPreferences;

      // Mettre à jour l'état local
      setTilePreferences(filteredPreferences);

      // Sauvegarder dans Firestore
      await updateDoc(doc(db, 'employees', currentUser.id), {
        tilePreferences: filteredPreferences
      });

    } catch (error) {
      // Erreur silencieuse lors de la sauvegarde
    }
  };

  // Fonction utilitaire pour calculer les jours jusqu'à l'anniversaire
  const getDaysUntilBirthday = (birthDate) => {
    if (!birthDate) return null;

    const today = new Date();
    const birth = new Date(birthDate);
    const nextBirthday = new Date(today.getFullYear(), birth.getMonth(), birth.getDate());

    // Si l'anniversaire est déjà passé cette année, on passe à l'année suivante
    if (nextBirthday < today) {
      nextBirthday.setFullYear(today.getFullYear() + 1);
    }

    const diffTime = Math.abs(nextBirthday - today);
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  // Fonction pour calculer l'âge
  const calculateAge = (birthDate) => {
    const today = new Date();
    const birth = new Date(birthDate);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age + 1; // +1 car on veut l'âge qu'ils vont avoir
  };

  // Dans le composant Dashboard, ajoutons l'état et le useEffect
  const [upcomingBirthdays, setUpcomingBirthdays] = useState([]);

  useEffect(() => {
    const fetchBirthdays = async () => {
      const employeesSnapshot = await getDocs(collection(db, 'employees'));
      const clientsSnapshot = await getDocs(collection(db, 'clients'));

      const birthdays = [];

      // Récupérer les anniversaires des employés
      employeesSnapshot.forEach((doc) => {
        const data = doc.data();
        if (data.birthDate) {
          const daysUntil = getDaysUntilBirthday(data.birthDate);
          if (daysUntil !== null && daysUntil <= 7) {
            birthdays.push({
              id: doc.id,
              name: `${data.firstName} ${data.lastName}`,
              birthDate: data.birthDate,
              daysUntil,
              type: 'employee',
              age: calculateAge(data.birthDate)
            });
          }
        }
      });

      // Récupérer les anniversaires des clients
      clientsSnapshot.forEach((doc) => {
        const data = doc.data();
        if (data.birthDate) {
          const daysUntil = getDaysUntilBirthday(data.birthDate);
          if (daysUntil !== null && daysUntil <= 7) {
            birthdays.push({
              id: doc.id,
              name: `${data.firstName} ${data.lastName}`,
              birthDate: data.birthDate,
              daysUntil,
              type: 'client',
              age: calculateAge(data.birthDate)
            });
          }
        }
      });

      // Trier par nombre de jours jusqu'à l'anniversaire
      birthdays.sort((a, b) => a.daysUntil - b.daysUntil);
      setUpcomingBirthdays(birthdays);
    };

    fetchBirthdays();
  }, [db]);

  const [plannedHours, setPlannedHours] = useState(0);

  useEffect(() => {
    const calculatePlannedHours = async () => {
      if (!db || !currentUser) {
        setPlannedHours(0);
        return;
      }

      try {
        const now = new Date();
        const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0);
        const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);

        const appointmentsRef = collection(db, 'appointments');
        const q = query(
          appointmentsRef,
          where('employeeId', '==', currentUser.id),
          where('date', '>=', Timestamp.fromDate(startOfMonth)),
          where('date', '<=', Timestamp.fromDate(endOfMonth))
        );

        const querySnapshot = await getDocs(q);
        let totalMinutes = 0;

        querySnapshot.docs.forEach(doc => {
          const appointment = doc.data();
          const [startHours, startMinutes] = appointment.startTime.split(':');
          const [endHours, endMinutes] = appointment.endTime.split(':');

          const start = parseInt(startHours) * 60 + parseInt(startMinutes);
          const end = parseInt(endHours) * 60 + parseInt(endMinutes);

          totalMinutes += end - start;
        });

        setPlannedHours(totalMinutes / 60);

      } catch (error) {
        setPlannedHours(0);
      }
    };

    calculatePlannedHours();
  }, [db, currentUser]);




  const [workedHours, setWorkedHours] = useState(0);

  useEffect(() => {
    const calculateWorkedHours = async () => {
      if (!db || !currentUser) {
        setWorkedHours(0);
        return;
      }

      try {
        const now = new Date();
        const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0);
        const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);

        const appointmentsRef = collection(db, 'appointments');
        const q = query(
          appointmentsRef,
          where('employeeId', '==', currentUser.id),
          where('date', '>=', Timestamp.fromDate(startOfMonth)),
          where('date', '<=', Timestamp.fromDate(endOfMonth)),
          where('status', '==', 'completed')
        );

        const querySnapshot = await getDocs(q);
        let totalMinutes = 0;

        querySnapshot.docs.forEach(doc => {
          const appointment = doc.data();
          const [startHours, startMinutes] = appointment.startTime.split(':');
          const [endHours, endMinutes] = appointment.endTime.split(':');

          const start = parseInt(startHours) * 60 + parseInt(startMinutes);
          const end = parseInt(endHours) * 60 + parseInt(endMinutes);

          totalMinutes += end - start;
        });

        setWorkedHours(totalMinutes / 60);

      } catch (error) {
        setWorkedHours(0);
      }
    };

    calculateWorkedHours();
  }, [db, currentUser]);

  // Fonction de formatage des heures
  const formatHours = (hours) => {
    if (isNaN(hours)) return "0h00";
    const wholeHours = Math.floor(hours);
    const minutes = Math.round((hours - wholeHours) * 60);
    return `${wholeHours}h${minutes.toString().padStart(2, '0')}`;
  };


  // INDICATEUR DE CHARGEMENT UNIFIÉ - Un seul écran de chargement
  //if (preferencesLoading) {
  //return (
  //<div className="dashboard-container">
  //<div className="flex items-center justify-center h-screen">
  //<div className="text-center">
  //<div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-500 mx-auto mb-4"></div>
  //<h2 className="text-xl font-semibold text-gray-700 mb-2">Chargement du tableau de bord</h2>
  //<p className="text-gray-500">Veuillez patienter...</p>
  //</div>
  //</div>
  //</div>
  //);
  //}

  // Dashboard prêt à afficher

  return (
    <div className="dashboard-container p-6 min-h-screen bg-gray-50 dark:bg-slate-900 text-gray-900 dark:text-slate-100 transition-colors duration-300">
      <div className="mb-8 flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          {/* Avatar */}
          {currentUser?.photoURL ? (
            <img
              src={currentUser.photoURL}
              alt="avatar"
              className="w-14 h-14 rounded-full object-cover border-2 border-blue-400 dark:border-blue-500 shadow-md flex-shrink-0"
            />
          ) : (
            <div
              className="w-14 h-14 rounded-full flex items-center justify-center text-white text-xl font-bold shadow-md flex-shrink-0"
              style={{ backgroundColor: currentUser?.color || '#6366f1' }}
            >
              {currentUser?.firstName?.charAt(0)?.toUpperCase() || '?'}
            </div>
          )}
          <div>
            <h1 className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-500 to-purple-500">
              Bonjour {currentUser?.firstName || 'Utilisateur'} 👋
            </h1>
            <div className="flex items-center gap-2 mt-0.5">
              {currentUser?.role && (
                <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 capitalize">
                  {currentUser.role}
                </span>
              )}
              <p className="text-gray-500 dark:text-slate-400 text-sm">
                Voici ce qui se passe aujourd'hui.
              </p>
            </div>
          </div>
        </div>
        <button
          onClick={() => setShowCustomizeModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-slate-800 text-gray-700 dark:text-slate-200 rounded-lg shadow-sm border border-gray-300 dark:border-slate-700 hover:bg-gray-50 dark:hover:bg-slate-700 transition"
        >
          <Settings size={18} />
          <span>Personnaliser</span>
        </button>
      </div>

      <div className="dashboard-grid grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {tilePreferences.dateTime && (
          <PremiumTile
            title="Date & Heure"
            icon={Clock}
            color="blue"
            className="md:col-span-1"
            delay={0.1}
          >
            <div className="flex flex-col items-center justify-center h-full py-2">
              <div className="text-blue-500 dark:text-blue-200 font-medium mb-1">{formatDate(currentTime)}</div>
              <div className="text-5xl font-bold tracking-tighter text-gray-900 dark:text-white">{formatTime(currentTime)}</div>
            </div>
          </PremiumTile>
        )}

        {tilePreferences.monEspace && (
          <PremiumTile
            title="Mon espace"
            icon={Home}
            color="indigo"
            className="md:col-span-3"
            delay={0.2}
          >
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-2">
              <div className="bg-indigo-50 dark:bg-slate-800/50 backdrop-blur-md rounded-xl p-4 border border-indigo-100 dark:border-slate-700/50">
                <span className="text-indigo-500 dark:text-indigo-300 text-xs font-semibold uppercase tracking-wider">Aujourd'hui</span>
                <div className="text-3xl font-bold mt-1 text-gray-900 dark:text-white">{todayAppointmentsCount}</div>
                <div className="text-xs text-gray-500 dark:text-slate-400 mt-1">Rendez-vous</div>
              </div>
              <div className="bg-indigo-50 dark:bg-slate-800/50 backdrop-blur-md rounded-xl p-4 border border-indigo-100 dark:border-slate-700/50">
                <span className="text-indigo-500 dark:text-indigo-300 text-xs font-semibold uppercase tracking-wider">Demain</span>
                <div className="text-3xl font-bold mt-1 text-gray-900 dark:text-white">{tomorrowAppointmentsCount}</div>
                <div className="text-xs text-gray-500 dark:text-slate-400 mt-1">Rendez-vous</div>
              </div>
              <div className="bg-indigo-50 dark:bg-slate-800/50 backdrop-blur-md rounded-xl p-4 border border-indigo-100 dark:border-slate-700/50">
                <span className="text-indigo-500 dark:text-indigo-300 text-xs font-semibold uppercase tracking-wider">Occupation</span>
                <div className="text-3xl font-bold mt-1 text-gray-900 dark:text-white">{occupationRate}%</div>
                <div className="text-xs text-gray-500 dark:text-slate-400 mt-1">Taux actuel</div>
              </div>
              <div className="bg-indigo-50 dark:bg-slate-800/50 backdrop-blur-md rounded-xl p-4 border border-indigo-100 dark:border-slate-700/50">
                <span className="text-indigo-500 dark:text-indigo-300 text-xs font-semibold uppercase tracking-wider">Prochain</span>
                <div className="text-3xl font-bold mt-1 text-gray-900 dark:text-white">{nextAppointmentTime}</div>
                <div className="text-xs text-gray-500 dark:text-slate-400 mt-1">Heure du RDV</div>
              </div>
            </div>

            {/* Barre de synthèse mensuelle */}
            <div className="mt-4 grid grid-cols-3 gap-3 px-1">
              <div className="flex items-center gap-2 text-xs text-gray-600 dark:text-slate-400">
                <Clock size={13} className="text-indigo-400 flex-shrink-0" />
                <span><span className="font-semibold text-gray-800 dark:text-white">{formatHours(workedHours)}</span> travaillées ce mois</span>
              </div>
              <div className="flex items-center gap-2 text-xs text-gray-600 dark:text-slate-400">
                <TrendingUp size={13} className="text-cyan-400 flex-shrink-0" />
                <span><span className="font-semibold text-gray-800 dark:text-white">{formatHours(plannedHours)}</span> planifiées</span>
              </div>
              {facturesData.montantEnAttente > 0 && (
                <div className="flex items-center gap-2 text-xs text-gray-600 dark:text-slate-400">
                  <AlertTriangle size={13} className="text-amber-400 flex-shrink-0" />
                  <span><span className="font-semibold text-amber-600 dark:text-amber-400">{facturesData.montantEnAttente.toFixed(0)}€</span> à encaisser</span>
                </div>
              )}
            </div>

            <div className="mt-4 grid grid-cols-3 gap-3">
              <div className="col-span-2">
                <button
                  onClick={() => navigate('/overview')}
                  className="w-full py-3 bg-indigo-500/20 text-indigo-600 dark:text-indigo-300 border border-indigo-300 dark:border-indigo-500/30 font-bold rounded-xl hover:bg-indigo-500/30 transition-colors flex items-center justify-center gap-2"
                >
                  <Eye size={16} />
                  Vue d'ensemble détaillée
                </button>
              </div>
              <div className="col-span-1">
                <div className="w-full py-3 bg-gray-50 dark:bg-slate-800/50 border border-gray-200 dark:border-slate-700/50 rounded-xl text-center">
                  <div className="text-xs text-gray-500 dark:text-slate-400">Revenus/objectif</div>
                  <div className="text-xs font-bold text-gray-800 dark:text-white mt-0.5">
                    {plannedHours > 0 ? Math.round((workedHours / plannedHours) * 100) : 0}%
                  </div>
                </div>
              </div>
            </div>
          </PremiumTile>
        )}

        {/* Tuile vueEnsemble fusionnée dans monEspace — supprimée ici */}

        {tilePreferences.prochainRendezVous && (
          <PremiumTile
            title="Prochains rendez-vous"
            icon={Calendar}
            color="purple"
            className="md:col-span-2 md:row-span-2"
            delay={0.4}
          >
            <div className="space-y-3 mt-2 max-h-[400px] overflow-y-auto pr-2 custom-scrollbar-light">
              {appointments.length > 0 ? (
                appointments.map(appointment => {
                  const now = new Date();
                  const isOngoing = now >= appointment.startDateTime && now < appointment.endDateTime;

                  return (
                    <div
                      key={appointment.id}
                      className={`p-4 rounded-xl border transition-all cursor-pointer ${isOngoing
                          ? 'bg-purple-500/20 text-purple-800 dark:text-purple-100 border-purple-400/50 dark:border-purple-500/50 shadow-lg scale-[1.02]'
                          : 'bg-gray-50 dark:bg-slate-800/50 border-gray-200 dark:border-slate-700/50 hover:bg-gray-100 dark:hover:bg-slate-800 text-gray-700 dark:text-slate-300'
                        }`}
                      onClick={() => setSelectedAppointment(appointment)}
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <div className="font-bold text-lg mb-1 text-gray-900 dark:text-white">{appointment.clientName}</div>
                          <div className={`text-sm flex items-center gap-1 ${isOngoing ? 'text-purple-600 dark:text-purple-300' : 'text-gray-500 dark:text-slate-400'}`}>
                            <MapPin size={14} />
                            {appointment.clientAddress ? formatAddress(appointment.clientAddress) : 'Adresse non spécifiée'}
                          </div>
                        </div>
                        <div className={`text-right ${isOngoing ? 'text-purple-600 dark:text-purple-300' : 'text-gray-500 dark:text-slate-400'}`}>
                          <div className="font-bold text-lg text-gray-900 dark:text-white">{appointment.startTime}</div>
                          <div className="text-xs opacity-75">{formatAppointmentDate(appointment.date, '')}</div>
                        </div>
                      </div>
                      {isOngoing && (
                        <div className="mt-2 inline-block px-2 py-1 bg-purple-500 text-white text-xs rounded-md font-medium uppercase tracking-wider">
                          En cours
                        </div>
                      )}
                    </div>
                  );
                })
              ) : (
                <div className="flex flex-col items-center justify-center h-40 text-gray-400 dark:text-slate-500 bg-gray-50 dark:bg-slate-800/30 rounded-xl border border-gray-200 dark:border-slate-800">
                  <Calendar size={32} className="mb-2 opacity-50" />
                  <p>Aucun rendez-vous à venir</p>
                </div>
              )}
            </div>
          </PremiumTile>
        )}

        {tilePreferences.gestionCollaborateurs && (
          <PremiumTile
            title="Collaborateurs"
            icon={Users}
            color="emerald"
            className="md:col-span-1"
            delay={0.5}
          >
            <div className="flex flex-col items-center justify-center py-4">
              <span className="text-5xl font-bold text-gray-900 dark:text-white mb-2">{activeEmployees}</span>
              <span className="text-emerald-600 dark:text-emerald-300">Actifs actuellement</span>
            </div>
          </PremiumTile>
        )}

        {tilePreferences.sommairePaie && (
          <PremiumTile
            title="Paie & Objectifs"
            icon={CreditCard}
            color="slate"
            className="md:col-span-1"
            delay={0.5}
          >
            <div className="text-center py-2">
              <span className="text-4xl font-bold text-gray-900 dark:text-white block mb-1">
                {calculateRemainingDaysInMonth()}
              </span>
              <span className="text-gray-500 dark:text-slate-400 text-sm">
                {calculateRemainingDaysInMonth() > 1 ? 'Jours restants' : 'Jour restant'}
              </span>
            </div>

            <div className="mt-4 space-y-3 bg-gray-50 dark:bg-slate-900/50 p-3 rounded-xl border border-gray-200 dark:border-slate-800">
              <div className="flex justify-between items-center text-sm">
                <span className="text-gray-500 dark:text-slate-400">Travaillé</span>
                <span className="font-bold text-gray-900 dark:text-white">{formatHours(workedHours)}</span>
              </div>
              <div className="w-full bg-gray-200 dark:bg-slate-700/50 rounded-full h-2">
                <div
                  className="bg-emerald-500 h-2 rounded-full transition-all duration-1000 shadow-[0_0_10px_rgba(16,185,129,0.5)]"
                  style={{ width: `${plannedHours === 0 ? 0 : Math.min((workedHours / plannedHours) * 100, 100)}%` }}
                ></div>
              </div>
              <div className="flex justify-between items-center text-sm">
                <span className="text-gray-500 dark:text-slate-400">Objectif</span>
                <span className="font-bold text-gray-700 dark:text-slate-300">{formatHours(plannedHours)}</span>
              </div>
            </div>
          </PremiumTile>
        )}

        {tilePreferences.anniversaires && (
          <PremiumTile
            title="Anniversaires"
            icon={Cake}
            color="rose"
            className="md:col-span-2"
            delay={0.6}
          >
            <div className="space-y-3 max-h-[200px] overflow-y-auto pr-2 custom-scrollbar-light">
              {upcomingBirthdays.length > 0 ? (
                upcomingBirthdays.map((birthday) => (
                  <div key={birthday.id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-slate-800/50 rounded-xl border border-gray-200 dark:border-slate-700/50 hover:bg-white dark:hover:bg-slate-800 transition-colors">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-lg ${birthday.type === 'employee' ? 'bg-blue-500/20 text-blue-500 dark:text-blue-400' : 'bg-green-500/20 text-green-500 dark:text-green-400'}`}>
                        {birthday.name.charAt(0)}
                      </div>
                      <div>
                        <div className="font-semibold text-gray-900 dark:text-white">{birthday.name}</div>
                        <div className="text-xs text-rose-500 dark:text-rose-300">
                          {birthday.daysUntil === 0 ? "Aujourd'hui !" : `Dans ${birthday.daysUntil} jours`} • {birthday.age} ans
                        </div>
                      </div>
                    </div>
                    <span className={`px-2 py-1 rounded text-xs font-medium ${birthday.type === 'employee' ? 'bg-blue-500/10 text-blue-600 dark:text-blue-300' : 'bg-green-500/10 text-green-600 dark:text-green-300'
                      }`}>
                      {birthday.type === 'employee' ? 'Employé' : 'Client'}
                    </span>
                  </div>
                ))
              ) : (
                <div className="text-center py-6 text-gray-400 dark:text-slate-500">
                  <Cake size={32} className="mx-auto mb-2 opacity-50" />
                  <p>Aucun anniversaire cette semaine</p>
                </div>
              )}
            </div>
          </PremiumTile>
        )}

        {/* Tuiles optionnelles / secondaires */}
        {tilePreferences.suiviPresences && (
          <PremiumTile
            title="Présences"
            icon={User}
            color="emerald"
            className="md:col-span-1"
            delay={0.7}
          >
            <div className="flex flex-col items-center justify-center py-4 text-emerald-400 dark:text-emerald-500">
              <div className="w-10 h-10 rounded-full bg-emerald-50 dark:bg-emerald-900/20 flex items-center justify-center mb-2">
                <RefreshCw size={20} className="opacity-70" />
              </div>
              <span className="text-sm font-medium text-gray-600 dark:text-slate-400">Rien à signaler</span>
            </div>
          </PremiumTile>
        )}

        {tilePreferences.suiviTaches && (
          <PremiumTile
            title="Tâches"
            icon={CheckSquare}
            color="amber"
            className="md:col-span-1"
            delay={0.7}
          >
            <div className="flex flex-col items-center justify-center py-4 text-amber-400 dark:text-amber-500">
              <div className="w-10 h-10 rounded-full bg-amber-50 dark:bg-amber-900/20 flex items-center justify-center mb-2">
                <CheckSquare size={20} className="opacity-70" />
              </div>
              <span className="text-sm font-medium text-gray-600 dark:text-slate-400">Aucune tâche</span>
              <button
                onClick={() => navigate('/dashboard/TaskManager')}
                className="mt-3 text-xs text-amber-600 dark:text-amber-400 hover:underline"
              >
                Gérer les tâches →
              </button>
            </div>
          </PremiumTile>
        )}

        {tilePreferences.suiviFactures && (
          <PremiumTile
            title="Factures"
            icon={FileText}
            color={facturesData.enRetard > 0 ? "rose" : facturesData.enAttente > 0 ? "amber" : "emerald"}
            className="md:col-span-1"
            delay={0.7}
          >
            {facturesData.total > 0 ? (
              <div className="space-y-3 mt-1">
                {facturesData.enRetard > 0 && (
                  <div className="flex items-center justify-between p-2.5 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-700/40">
                    <div className="flex items-center gap-2">
                      <AlertTriangle size={14} className="text-red-500" />
                      <span className="text-xs font-medium text-red-700 dark:text-red-300">En retard</span>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-bold text-red-600 dark:text-red-400">{facturesData.enRetard}</div>
                      <div className="text-xs text-red-500 dark:text-red-400">{facturesData.montantEnRetard.toFixed(0)}€</div>
                    </div>
                  </div>
                )}
                {facturesData.enAttente > 0 && (
                  <div className="flex items-center justify-between p-2.5 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-700/40">
                    <div className="flex items-center gap-2">
                      <CreditCard size={14} className="text-amber-500" />
                      <span className="text-xs font-medium text-amber-700 dark:text-amber-300">En attente</span>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-bold text-amber-600 dark:text-amber-400">{facturesData.enAttente}</div>
                      <div className="text-xs text-amber-500 dark:text-amber-400">{facturesData.montantEnAttente.toFixed(0)}€</div>
                    </div>
                  </div>
                )}
                {facturesData.enRetard === 0 && facturesData.enAttente === 0 && (
                  <div className="flex flex-col items-center justify-center py-3 text-emerald-500 dark:text-emerald-400">
                    <FileText size={22} className="mb-1 opacity-70" />
                    <span className="text-xs font-medium">Tout est à jour</span>
                  </div>
                )}
                <div className="text-xs text-gray-400 dark:text-gray-500 text-right">{facturesData.total} facture{facturesData.total > 1 ? 's' : ''} au total</div>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-6 text-gray-400 dark:text-slate-500">
                <FileText className="mb-2 opacity-50" size={24} />
                <span className="text-sm">Aucune facture</span>
              </div>
            )}
          </PremiumTile>
        )}

        {tilePreferences.suiviDevis && (
          <PremiumTile
            title="Devis"
            icon={FileText}
            color="cyan"
            className="md:col-span-1"
            delay={0.7}
          >
            <div className="flex flex-col items-center justify-center py-4 text-cyan-400 dark:text-cyan-500">
              <div className="w-10 h-10 rounded-full bg-cyan-50 dark:bg-cyan-900/20 flex items-center justify-center mb-2">
                <FileText size={20} className="opacity-70" />
              </div>
              <span className="text-sm font-medium text-gray-600 dark:text-slate-400">Aucun devis</span>
            </div>
          </PremiumTile>
        )}

        {tilePreferences.suiviDocuments && (
          <PremiumTile
            title="Documents"
            icon={FileText}
            color="indigo"
            className="md:col-span-1"
            delay={0.7}
          >
            <div className="flex flex-col items-center justify-center py-4 text-indigo-400 dark:text-indigo-500">
              <div className="w-10 h-10 rounded-full bg-indigo-50 dark:bg-indigo-900/20 flex items-center justify-center mb-2">
                <Newspaper size={20} className="opacity-70" />
              </div>
              <span className="text-sm font-medium text-gray-600 dark:text-slate-400">Aucun document</span>
            </div>
          </PremiumTile>
        )}

        {tilePreferences.suiviContacts && (
          <PremiumTile
            title="Contacts"
            icon={Users}
            color="purple"
            className="md:col-span-1"
            delay={0.7}
          >
            <div className="flex flex-col items-center justify-center py-4 text-purple-400 dark:text-purple-500">
              <div className="w-10 h-10 rounded-full bg-purple-50 dark:bg-purple-900/20 flex items-center justify-center mb-2">
                <Users size={20} className="opacity-70" />
              </div>
              <span className="text-sm font-medium text-gray-600 dark:text-slate-400">Aucun contact</span>
            </div>
          </PremiumTile>
        )}

        {tilePreferences.rapportsIntervenants && (
          <PremiumTile
            title="Rapports d'activité"
            icon={FileText}
            color="amber"
            className="md:col-span-3"
            delay={0.8}
          >
            <div className="md:flex items-center justify-between gap-6">
              <div className="flex-1 space-y-2 mb-4 md:mb-0">
                <div className="flex items-center gap-2 text-amber-700 dark:text-amber-100/80">
                  <BarChart3 size={16} className="text-amber-500 dark:text-amber-400" /> Statiqustiques mensuelles
                </div>
                <div className="flex items-center gap-2 text-amber-700 dark:text-amber-100/80">
                  <DollarSign size={16} className="text-amber-500 dark:text-amber-400" /> Analyse de productivité
                </div>
                <div className="flex items-center gap-2 text-amber-700 dark:text-amber-100/80">
                  <Users size={16} className="text-amber-500 dark:text-amber-400" /> Performance équipes
                </div>
              </div>
              <button
                onClick={() => navigate('/dashboard/employeeReports')}
                className="bg-amber-500/20 text-amber-600 dark:text-amber-300 border border-amber-300 dark:border-amber-500/30 font-bold py-3 px-6 rounded-xl hover:bg-amber-500/30 transition-colors shadow-lg whitespace-nowrap"
              >
                Voir les rapports
              </button>
            </div>
          </PremiumTile>
        )}
      </div>

      <CustomizeModal
        isOpen={showCustomizeModal}
        onClose={() => setShowCustomizeModal(false)}
        preferences={tilePreferences}
        onSave={saveTilePreferences}
        roleBasedTiles={roleBasedTiles}
      />

      <PopupNavigation
        isOpen={!!selectedAppointment}
        onClose={() => setSelectedAppointment(null)}
        address={formatAddress(selectedAppointment?.clientAddress) || "Adresse non disponible"}
      />
    </div>
  );
};




export default Dashboard;
